import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useCurrentTool } from '@/shared/hooks/useCurrentTool'
import { useGenerateReport } from '@/features/generation/useGenerateReport';
import { useFileUpload } from '@/features/generation/useFileUpload';
import { useAudioRecording } from '@/features/generation/useAudioRecording';
import { saveDraft } from '@/shared/lib/draftService'
import { useReportsContext } from '@/features/reports/ReportsContext';
import { setRecordingDuration, setRecordingStarted, setReportId } from '@/store/slices/ReportsSlice'
import { v4 as uuidv4 } from 'uuid';
import { emptyFormData, formDataFromSubmit } from '@/features/generation/mediaInputDraft'
import { MediaInputView } from '@/features/generation/MediaInputView'
import SilentAudioConfirmDialog from '@/features/generation/SilentAudioConfirmDialog'
import { useSilentAudioConfirm } from '@/shared/audio/useSilentAudioConfirm'

function MediaInput({ mode = 'record', isDisabled, setHidingtabs, toolId, activeDraft, setActiveDraft }) {
  const dispatch = useDispatch();
  const { nonAudioTool, toolName } = useCurrentTool();
  const { recordingSessionRef } = useReportsContext();
  const draftIdRef = useRef(null);
  const draftCreatedAtRef = useRef(null);
  const abandonRecordingRef = useRef(null);
  
  const {
    inputStatus,
    setInputStatus,
    generateReport,
    setGenerateReport,
    generating,
    uploading,
    recordingTime,
    setRecordingTime,
    recordedBlob,
    setRecordedBlob,
    selectedFile,
    setSelectedFile,
    audioDuration,
    setAudioDuration,
    finalising,
    setFinalising,
    handleResetState,
    handleGenerateReport,
    handleGoToReport,
    formatTime
  } = useGenerateReport(setHidingtabs, toolId);

  const { startRecording } = useAudioRecording();

  useEffect(() => {
    if (activeDraft && inputStatus === 'start' && !recordedBlob) {
      draftIdRef.current = activeDraft.id;
      draftCreatedAtRef.current = activeDraft.createdAt ?? Date.now();
      setRecordedBlob(activeDraft.audioBlob);
      setRecordingTime(activeDraft.recordingTime || 0);
      setHidingtabs(true);
      setInputStatus('stopped');
    }
  }, [activeDraft, inputStatus, recordedBlob, setRecordedBlob, setRecordingTime, setHidingtabs, setInputStatus]);

  const onFileSelected = (files, duration) => {
    setSelectedFile(files);
    setAudioDuration(duration);
    setHidingtabs(true);
    setInputStatus('stopped');
  };

  const { confirmIfSilent, dialogProps: silentAudioDialogProps } = useSilentAudioConfirm();

  const {
    fileInputRef,
    uploadModelOpen,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleBrowseClick,
    acceptedTypes
  } = useFileUpload(onFileSelected, isDisabled.loading || isDisabled.noWords, confirmIfSilent);

  const disabled = isDisabled.loading || isDisabled.noWords;

  const handleAbandonRecording = useCallback(async () => {
    await abandonRecordingRef.current?.();
    handleResetState();
    setHidingtabs(false);
    dispatch(setRecordingStarted(false));
    dispatch(setRecordingDuration(0));
    dispatch(setReportId(null));
  }, [dispatch, handleResetState, setHidingtabs]);

  useEffect(() => {
    if (mode !== 'record') return undefined;

    recordingSessionRef.current = {
      isActive: inputStatus === 'recording',
      abandon: handleAbandonRecording,
    };

    return () => {
      recordingSessionRef.current = { isActive: false, abandon: null };
    };
  }, [mode, inputStatus, handleAbandonRecording, recordingSessionRef]);

  const handleStart = async () => {
    if (disabled) return;

    if (mode === 'record') {
      setActiveDraft?.(null);
      draftIdRef.current = null;
      draftCreatedAtRef.current = null;
      const canRecord = await startRecording();
      if (canRecord) {
        setInputStatus('recording');
      } else {
        handleResetState();
      }
    } else {
      handleBrowseClick();
    }
  };

  const handleAudio = async (blob) => {
    setFinalising(false);

    setRecordedBlob(blob);
    setHidingtabs(true);
    setInputStatus('stopped');

    if (mode === 'record') {
      const id = draftIdRef.current || uuidv4();
      const createdAt = draftCreatedAtRef.current ?? Date.now();
      draftIdRef.current = id;
      draftCreatedAtRef.current = createdAt;
      const draftPayload = {
        id,
        audioBlob: blob,
        audioType: blob?.type ?? 'audio/webm',
        recordingTime: Math.floor(recordingTime || 0),
        toolId,
        formData: emptyFormData(),
        createdAt,
      };
      void saveDraft(draftPayload).catch((err) => {
        console.error('Failed to save draft:', err);
      });
    }
  };

  const onGenerateReport = async (data) => {
    const isRecordedAudio = mode === 'record' && data.recordedAudioBlob;
    if (isRecordedAudio) {
      if (!draftIdRef.current) {
        draftIdRef.current = uuidv4();
        draftCreatedAtRef.current = Date.now();
      }
      const id = draftIdRef.current;
      try {
        await saveDraft({
          id,
          audioBlob: data.recordedAudioBlob,
          audioType: data.recordedAudioBlob?.type ?? 'audio/webm',
          recordingTime: Math.floor(recordingTime || 0),
          toolId,
          formData: formDataFromSubmit(data, toolName),
          createdAt: draftCreatedAtRef.current ?? Date.now(),
        });
      } catch (err) {
        console.error('Failed to save draft:', err);
      }
    }

    setGenerateReport('generating');
    await handleGenerateReport({ ...data, draftId: draftIdRef.current });
  };

  return (
    <>
    <MediaInputView
      mode={mode}
      inputStatus={inputStatus}
      generateReport={generateReport}
      disabled={disabled}
      isDisabled={isDisabled}
      nonAudioTool={nonAudioTool}
      handleStart={handleStart}
      handleBrowseClick={handleBrowseClick}
      handleDrop={handleDrop}
      handleDragOver={handleDragOver}
      handleFileChange={handleFileChange}
      fileInputRef={fileInputRef}
      acceptedTypes={acceptedTypes}
      uploadModelOpen={uploadModelOpen}
      handleAudio={handleAudio}
      setRecordingTime={setRecordingTime}
      finalising={finalising}
      setFinalising={setFinalising}
      abandonRecordingRef={abandonRecordingRef}
      recordingTime={recordingTime}
      audioDuration={audioDuration}
      selectedFile={selectedFile}
      recordedBlob={recordedBlob}
      formatTime={formatTime}
      onGenerateReport={onGenerateReport}
      setGenerateReport={setGenerateReport}
      activeDraft={activeDraft}
      uploading={uploading}
      generating={generating}
      handleResetState={handleResetState}
      handleGoToReport={handleGoToReport}
      confirmIfSilent={confirmIfSilent}
    />
    <SilentAudioConfirmDialog {...silentAudioDialogProps} />
    </>
  );
}

export default MediaInput;
